import Image from "next/image";
import { useEffect, useState } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader

import { toCdnUrl } from "../../lib/cdn";
import { resolveImage } from "../../lib/media";
import Picture from "../Picture";
import "./index.module.css";
import Indicator from "./Indicator";
import NextArrow from "./NextArrow";
import PrevArrow from "./PrevArrow";

const DM2BuyCarousel = ({ store, product }) => {
  const [selectedItem, setSelectedItem] = useState(0);

  // Reset to first image when product changes
  useEffect(() => {
    setSelectedItem(0);
  }, [product?.id]);

  const photos = product?.productPhotos;

  // react-responsive-carousel computes the slide list's transform ONCE, in its constructor, from
  // the children it has at mount. Mounted before the product fetch resolves it sees zero slides,
  // treats index 0 as "past the last slide" (infiniteLoop), parks the list at translate3d(0) and
  // waits for a transition end that never comes; when the photos arrive it binds events but never
  // recomputes the position, so the wrap-around clone of the LAST photo is what the page lands on
  // while the first dot is lit. Not mounting it until there are photos, and keying it by product
  // so a client-side navigation between products gets a fresh constructor, fixes both.
  if (!photos?.length) return null;

  return (
    // `pdp-carousel` scopes the vertical-centring rules in globals.css to this carousel: the
    // home hero reuses .carousel with a height of its own.
    <div style={{ padding: "0px 18px" }} className="pdp-carousel z-10 mt-4">
      <Carousel
        key={product?.id}
        selectedItem={selectedItem}
        onChange={setSelectedItem}
        showThumbs={false}
        showStatus={false}
        infiniteLoop={true}
        showIndicators={product?.productPhotos?.length > 1 ? true : false}
        // centerMode={true}
        // centerSlidePercentage={110}
        renderArrowPrev={(onClickHandler) => {
          return <PrevArrow handler={onClickHandler} />;
        }}
        renderArrowNext={(onClickHandler) => {
          return <NextArrow handler={onClickHandler} />;
        }}
        renderIndicator={(onClickHandler, isSelected, index) => {
          if (product.allPhotos?.length < 2) return null;
          return <Indicator handler={onClickHandler} isSelected={isSelected} key={index} />;
        }}
        preventMovementUntilSwipeScrollTolerance
        swipeScrollTolerance={50}
      >
        {product.productPhotos &&
          product.productPhotos.map((photo, index) => {
            // Extract the image name from the original URL
            const imageName = photo.split("/").pop();
            const newPhoto = photo.includes("dm2buy")
              ? `https://dm2buy-drop-resized-gga4c6azekgcgngp.z02.azurefd.net/dm2buy/${imageName}`
              : toCdnUrl(photo);

            const fallBackPhoto = photo;
            const resolved = resolveImage(photo, product.productPhotosV2, "pdp");
            if (resolved.isV2) {
              return (
                <Picture
                  key={index}
                  resolved={resolved}
                  alt={product.name || ""}
                  width={360}
                  height={425}
                  // Slides are laid out off-viewport by react-responsive-carousel, so a lazy
                  // slide is blank on the first swipe. Legacy rendered every slide with
                  // `priority`; keep the first two eager as the compromise.
                  loading={index < 2 ? "eager" : "lazy"}
                  fetchpriority={index === 0 ? "high" : undefined}
                  imgClassName="mx-auto h-auto max-w-full"
                />
              );
            }

            return (
              <Image
                unoptimized="true"
                src={newPhoto}
                height={425}
                onLoad={() => {}}
                onError={(e) => {
                  // Only fallback if we're not already using the fallback
                  if (e.target.src !== fallBackPhoto) {
                    e.target.src = fallBackPhoto;
                  }
                  // If we're already on fallback and it failed, do nothing
                }}
                width={360}
                key={index}
                priority
              />
            );
          })}
      </Carousel>
    </div>
  );
};

export default DM2BuyCarousel;
